import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader as Loader2, Mail, Lock, User } from 'lucide-react';
import { useWorkspace } from '../context/WorkspaceContext';

export const Signup: React.FC = () => {
  const navigate = useNavigate();
  const { publicWorkspace } = useWorkspace();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const branding = publicWorkspace?.appearance?.loginPage || {};
  
  const bgColor = branding.backgroundColor || '#18181b';
  const isLightBg = isLightColor(bgColor);

  const logo = branding.logo || publicWorkspace?.logo || (isLightBg ? '/icon-blue.svg' : '/icon-white.svg');

  const cardBg      = isLightBg ? 'rgba(255,255,255,0.85)' : 'rgba(39,39,42,0.85)';
  const cardBorder  = isLightBg ? 'rgba(0,0,0,0.10)'      : 'rgba(255,255,255,0.08)';
  const labelColor  = branding.textColor || (isLightBg ? '#1f2937' : '#e4e4e7');
  const inputBg     = isLightBg ? 'rgba(243,244,246,0.8)' : 'rgba(9,9,11,0.5)';
  const inputBorder = isLightBg ? 'rgba(0,0,0,0.15)'     : 'rgba(113,113,122,0.6)';
  const inputText   = isLightBg ? '#111827' : '#ffffff';
  const inputPlaceholder = isLightBg ? '#9ca3af' : '#71717a';
  const mutedText   = isLightBg ? 'rgba(55,65,81,0.7)' : '#a1a1aa';
  const accentColor = branding.buttonColor || '#3b82f6';
  const footerBorderColor = isLightBg ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.05)';

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      const { data, error: signUpError } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/home`,
          data: {
            first_name: firstName,
            last_name: lastName,
            full_name: `${firstName} ${lastName}`.trim(),
            is_new_owner: true
          }
        }
      });
      if (signUpError) throw signUpError;

      if (data.user) {
        if (!data.session) {
          setShowSuccess(true);
        } else {
          navigate('/home');
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error) setError(err.message);
      else setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full py-2.5 rounded-lg text-sm outline-none transition-all";

  return (
    <div
      className="h-[100dvh] sm:h-auto sm:min-h-screen w-full overflow-y-auto flex flex-col p-4 transition-colors duration-500"
      style={{
        backgroundColor: bgColor,
        backgroundImage: branding.backgroundImage ? `url(${branding.backgroundImage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="w-full max-w-md m-auto py-6">
        {/* Logo + heading - Hidden on success */}
        {!showSuccess && (
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <img src={logo} alt={publicWorkspace?.name || 'AgencyPlus'} className="h-20 w-auto object-contain max-h-20" />
            </div>
            <h1 className="text-3xl font-bold mb-2" style={{ color: branding.textColor || (isLightBg ? '#111827' : '#ffffff') }}>
              Create Account
            </h1>
            <p style={{ color: mutedText }}>
              Sign up to get started with {publicWorkspace?.name || 'AgencyPlus'}
            </p>
          </div>
        )}

        {/* Card or Success View */}
        <div
          className={`${showSuccess ? 'text-center' : 'rounded-xl shadow-xl overflow-hidden backdrop-blur-sm'}`}
          style={showSuccess ? {} : { background: cardBg, border: `1px solid ${cardBorder}` }}
        >
          {/* Card Header - Hidden on success */}
          {!showSuccess && (
            <div className="px-6 pt-6 pb-2">
              <h2 className="text-xl font-semibold" style={{ color: labelColor }}>Sign Up</h2>
              <p className="text-sm mt-1" style={{ color: mutedText }}>Create your account to start building your brand</p>
            </div>
          )}

          {/* Card Body / Success Content */}
          <div className={`${showSuccess ? 'py-12 px-4' : 'px-6 py-4'}`}>
            {showSuccess ? (
              <div className="space-y-8">
                <div className="w-24 h-24 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto transition-transform duration-500 scale-110">
                  <Mail className="h-12 w-12 text-blue-500" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-4xl font-bold tracking-tight" style={{ color: branding.textColor || (isLightBg ? '#111827' : '#ffffff') }}>
                    Check your email
                  </h2>
                  <p className="text-xl leading-relaxed" style={{ color: mutedText }}>
                    We've sent a confirmation link to <br/>
                    <span className="font-semibold text-foreground break-all">{email}</span>.
                  </p>
                  <p className="text-lg opacity-80" style={{ color: mutedText }}>
                    Please click the link to activate your account.
                  </p>
                </div>
                <div className="pt-8">
                  <Link 
                    to="/login" 
                    className="inline-flex items-center justify-center px-10 py-4 rounded-xl text-lg font-semibold text-white transition-all hover:scale-105 active:scale-95 shadow-lg whitespace-nowrap"
                    style={{ backgroundColor: accentColor }}
                  >
                    Back to Sign In
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSignup} className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {/* Name row */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label htmlFor="firstName" className="block text-sm font-medium" style={{ color: labelColor }}>First Name</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: inputPlaceholder }} />
                      <input
                        id="firstName"
                        type="text"
                        placeholder="John"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        disabled={loading}
                        className={`${inputClass} pl-10 pr-4`}
                        style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: inputText }}
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label htmlFor="lastName" className="block text-sm font-medium" style={{ color: labelColor }}>Last Name</label>
                    <input
                      id="lastName"
                      type="text"
                      placeholder="Doe"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      disabled={loading}
                      className={`${inputClass} px-4`}
                      style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: inputText }}
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-1.5">
                  <label htmlFor="email" className="block text-sm font-medium" style={{ color: labelColor }}>Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: inputPlaceholder }} />
                    <input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={loading}
                      className={`${inputClass} pl-10 pr-4`}
                      style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: inputText }}
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="space-y-1.5">
                  <label htmlFor="password" className="block text-sm font-medium" style={{ color: labelColor }}>Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: inputPlaceholder }} />
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="At least 6 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                      className={`${inputClass} pl-10 pr-10`}
                      style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: inputText }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs"
                      style={{ color: inputPlaceholder }}
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="space-y-1.5">
                  <label htmlFor="confirmPassword" className="block text-sm font-medium" style={{ color: labelColor }}>Confirm Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: inputPlaceholder }} />
                    <input
                      id="confirmPassword"
                      type="password"
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={loading}
                      className={`${inputClass} pl-10 pr-4`}
                      style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: inputText }}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-60"
                  style={{ backgroundColor: accentColor }}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Creating account…
                    </>
                  ) : 'Create Account'}
                </button>
              </form>
            )}
          </div>

          {/* Footer - Hidden on success */}
          {!showSuccess && (
            <div
              className="px-6 py-4 flex justify-center"
              style={{ borderTop: `1px solid ${footerBorderColor}` }}
            >
              <p className="text-sm" style={{ color: mutedText }}>
                Already have an account?{' '}
                <Link to="/login" className="font-semibold hover:opacity-80 transition-opacity" style={{ color: accentColor }}>
                  Sign in
                </Link>
              </p>
            </div>
          )}
        </div>

        {/* Powered by Agency+ badge */}
        {publicWorkspace && (
          <p className="text-center text-sm mt-8 flex items-center justify-center gap-1" style={{ color: mutedText }}>
            Powered by{' '}
            <img src={isLightBg ? "/icon-blue.svg" : "/icon-white.svg"} alt="Agency+" className="h-4 w-4 opacity-50" />
            <span className="font-semibold opacity-50 text-xs tracking-tight">AgencyPlus</span>
          </p>
        )}
      </div>
    </div>
  );
};

function isLightColor(color: string): boolean {
  try {
    let r = 0, g = 0, b = 0;
    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      const full = hex.length === 3
        ? hex.split('').map(c => c + c).join('')
        : hex;
      r = parseInt(full.slice(0, 2), 16);
      g = parseInt(full.slice(2, 4), 16);
      b = parseInt(full.slice(4, 6), 16);
    } else if (color.startsWith('rgb')) {
      const match = color.match(/\d+/g);
      if (match) { r = +match[0]; g = +match[1]; b = +match[2]; }
    }
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminance > 160;
  } catch {
    return false;
  }
}
